import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

interface Product {
  id: string;
  name: string;
  slug: string;
  price: number;
  original_price: number | null;
  images: string[];
  in_stock: boolean;
  is_new: boolean;
  is_featured: boolean;
}

interface RecentlyViewedContextType {
  recentlyViewed: Product[];
  addToRecentlyViewed: (productId: string) => Promise<void>;
  loading: boolean;
}

const RecentlyViewedContext = createContext<RecentlyViewedContextType | undefined>(undefined);

const LOCAL_STORAGE_KEY = "leestone_recently_viewed";

export function RecentlyViewedProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [recentlyViewed, setRecentlyViewed] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch recently viewed products
  useEffect(() => {
    const fetchRecentlyViewed = async () => {
      setLoading(true);
      
      if (user) {
        // Fetch from database for logged-in users
        const { data: viewedData } = await (supabase as any)
          .from("recently_viewed")
          .select("product_id, viewed_at")
          .eq("user_id", user.id)
          .order("viewed_at", { ascending: false })
          .limit(10);

        if (viewedData && viewedData.length > 0) {
          const productIds = viewedData.map(item => item.product_id);
          const { data: products } = await (supabase as any)
            .from("products")
            .select("*")
            .in("id", productIds);

          if (products) {
            // Maintain order from recently_viewed
            const orderedProducts = productIds
              .map(id => products.find((p: any) => p.id === id))
              .filter(Boolean) as Product[];
            setRecentlyViewed(orderedProducts);
          }
        }
      } else {
        // Use localStorage for guests
        const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (stored) {
          const productIds = JSON.parse(stored) as string[];
          if (productIds.length > 0) {
            const { data } = await (supabase as any)
              .from("products")
              .select("*")
              .in("id", productIds);
            
            if (data) {
              // Maintain the order from localStorage
              const orderedProducts = productIds
                .map(id => data.find((p: any) => p.id === id))
                .filter(Boolean) as Product[];
              setRecentlyViewed(orderedProducts);
            }
          }
        }
      }
      setLoading(false);
    };

    fetchRecentlyViewed();
  }, [user]);

  const addToRecentlyViewed = async (productId: string) => {
    if (user) {
      // Insert to database for logged-in users (delete old one first to update timestamp)
      await (supabase as any)
        .from("recently_viewed")
        .delete()
        .eq("user_id", user.id)
        .eq("product_id", productId);
        
      await (supabase as any)
        .from("recently_viewed")
        .insert({ user_id: user.id, product_id: productId });

      // Refetch to update state
      const { data: viewedData } = await (supabase as any)
        .from("recently_viewed")
        .select("product_id, viewed_at")
        .eq("user_id", user.id)
        .order("viewed_at", { ascending: false })
        .limit(10);

      if (viewedData && viewedData.length > 0) {
        const productIds = viewedData.map(item => item.product_id);
        const { data: products } = await (supabase as any)
          .from("products")
          .select("*")
          .in("id", productIds);

        if (products) {
          const orderedProducts = productIds
            .map(id => products.find((p: any) => p.id === id))
            .filter(Boolean) as Product[];
          setRecentlyViewed(orderedProducts);
        }
      }
    } else {
      // Use localStorage for guests
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
      let productIds: string[] = stored ? JSON.parse(stored) : [];
      
      // Remove if already exists, then add to front
      productIds = productIds.filter(id => id !== productId);
      productIds.unshift(productId);
      productIds = productIds.slice(0, 10); // Keep only 10
      
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(productIds));

      // Fetch product details
      const { data } = await (supabase as any)
        .from("products")
        .select("*")
        .in("id", productIds);

      if (data) {
        const orderedProducts = productIds
          .map(id => data.find((p: any) => p.id === id))
          .filter(Boolean) as Product[];
        setRecentlyViewed(orderedProducts);
      }
    }
  };

  return (
    <RecentlyViewedContext.Provider value={{ recentlyViewed, addToRecentlyViewed, loading }}>
      {children}
    </RecentlyViewedContext.Provider>
  );
}

export function useRecentlyViewed() {
  const context = useContext(RecentlyViewedContext);
  if (context === undefined) {
    throw new Error("useRecentlyViewed must be used within a RecentlyViewedProvider");
  }
  return context;
}
